import { Controller } from "@hotwired/stimulus"

// File-upload question field (checkout / attendee registration).
// Replaces the raw native <input type=file> with a DS drop-zone (empty state)
// and a preview chip (selected / existing state). The native input stays in
// the DOM, visually hidden via clip (see .fu-input) so it keeps tab order and
// works with Playwright setInputFiles. Validation mirrors the server rules
// (TicketQuestionResponse file validation): an oversize or wrong-type file is
// rejected client-side, the input is cleared, and an error is shown.
//
// Drag-and-drop is supported: dragover/dragleave toggle the .is-dragover zone
// state, and drop assigns the dropped file to the native input (via DataTransfer)
// then runs the SAME validate -> preview/reject path as the native picker.
//
// Targets: input, zone, preview, error
// Values:
//   maxSize     (Number)  max bytes (data-max-size on the input)
//   accept      (String)  comma-separated extensions, e.g. ".jpeg,.jpg"
//   existingName (String) filename of an already-attached blob (editing)
//   existingUrl  (String) blob url of an already-attached file (editing)
//   existingImage (Boolean) whether the existing blob is an image
export default class extends Controller {
  static targets = ["input", "zone", "preview", "error"]
  static values = {
    maxSize: Number,
    accept: String,
    existingName: String,
    existingUrl: String,
    existingImage: Boolean,
    previewAlt: String,
    currentNote: String,
    replaceLabel: String,
    removeLabel: String,
    errorTooLarge: String,
    errorWrongType: String,
    inputId: String,
  }

  connect() {
    this.objectUrl = null
    // Editing an existing answer: seed the preview chip from the stored blob.
    if (this.hasExistingNameValue && this.existingNameValue) {
      this.renderExistingPreview()
    }
  }

  disconnect() {
    this.releaseObjectUrl()
  }

  // change on the native input — validate first, then preview or reject.
  change() {
    const file = this.inputTarget.files && this.inputTarget.files[0]
    if (!file) return

    const sizeError = this.validateSize(file)
    if (sizeError) return this.reject(sizeError, "file-warning")

    const typeError = this.validateType(file)
    if (typeError) return this.reject(typeError, "file-x")

    this.clearError()
    this.renderSelectedPreview(file)
  }

  // --- drag & drop -------------------------------------------------------
  // dragover MUST preventDefault, otherwise the browser handles the drop
  // itself (navigates to/opens the file) and our #drop never fires.

  dragover(event) {
    event.preventDefault()
    this.zoneTarget.classList.add("is-dragover")
  }

  dragleave(event) {
    if (event) event.preventDefault()
    this.zoneTarget.classList.remove("is-dragover")
  }

  drop(event) {
    event.preventDefault()
    this.zoneTarget.classList.remove("is-dragover")
    const files = event.dataTransfer && event.dataTransfer.files
    if (!files || !files.length) return
    // Mirror native selection: assign the dropped file to the input via a
    // DataTransfer, then reuse the SAME validate -> preview/reject path as the
    // native picker (no duplicated validation, no new XSS surface — the file
    // flows through the existing input + escape()'d preview).
    // Single-file field (native input isn't `multiple`), so take the first only.
    const dt = new DataTransfer()
    dt.items.add(files[0])
    this.inputTarget.files = dt.files
    this.change()
  }

  // Remove button — clear input, revoke url, return to the empty zone.
  remove(event) {
    if (event) event.preventDefault()
    this.inputTarget.value = ""
    this.releaseObjectUrl()
    this.clearError()
    this.showZone()
  }

  // --- validation (mirrors server rules) ---------------------------------

  validateSize(file) {
    if (!this.hasMaxSizeValue || this.maxSizeValue <= 0) return null
    if (file.size <= this.maxSizeValue) return null
    return this.errorTooLargeValue
      .replace("%{size}", this.humanSize(file.size))
      .replace("%{limit}", this.humanSize(this.maxSizeValue))
  }

  validateType(file) {
    const accept = (this.acceptValue || "").trim()
    if (!accept) return null
    const allowed = accept.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean)
    if (allowed.length === 0) return null
    const name = (file.name || "").toLowerCase()
    const ok = allowed.some((ext) => {
      const dotted = ext.startsWith(".") ? ext : "." + ext
      return name.endsWith(dotted)
    })
    if (ok) return null
    const pretty = allowed.map((e) => (e.startsWith(".") ? e : "." + e)).join(", ")
    return this.errorWrongTypeValue.replace("%{types}", pretty)
  }

  reject(message, iconName) {
    // Do NOT accept an invalid file — clear it so nothing is submitted.
    this.inputTarget.value = ""
    this.releaseObjectUrl()
    this.showZone()
    this.zoneTarget.classList.add("error")
    this.errorTarget.innerHTML =
      this.iconSvg("alert-circle", 14) + "<span>" + this.escape(message) + "</span>"
    this.errorTarget.hidden = false
    // Move focus to the zone so a keyboard/screen-reader user hears the error.
    this.inputTarget.focus()
  }

  clearError() {
    this.zoneTarget.classList.remove("error")
    this.errorTarget.hidden = true
    this.errorTarget.innerHTML = ""
  }

  // --- rendering ---------------------------------------------------------

  renderSelectedPreview(file) {
    const isImage = (file.type || "").startsWith("image/")
    let thumbInner
    if (isImage) {
      this.releaseObjectUrl()
      this.objectUrl = URL.createObjectURL(file)
      const alt = this.escape(this.previewAltValue || "Uploaded file preview")
      thumbInner = `<img src="${this.objectUrl}" alt="${alt}">`
    } else {
      thumbInner = this.iconSvg("file-text", 26)
    }
    this.paintPreview({
      thumbInner,
      filename: file.name,
      meta: this.humanSize(file.size),
      note: null,
      showRemove: true,
    })
  }

  renderExistingPreview() {
    let thumbInner
    if (this.existingImageValue && this.existingUrlValue) {
      const alt = this.escape(this.previewAltValue || "Current file preview")
      thumbInner = `<img src="${this.escape(this.existingUrlValue)}" alt="${alt}">`
    } else {
      thumbInner = this.iconSvg("file-text", 26)
    }
    this.paintPreview({
      thumbInner,
      filename: this.existingNameValue,
      meta: null,
      note: this.currentNoteValue,
      // An already-answered required field keeps Replace only (no Remove) so
      // the answer can't be blanked — matches the existing server behavior
      // where the field is not re-required while a blob is attached.
      showRemove: false,
    })
  }

  paintPreview({ thumbInner, filename, meta, note, showRemove }) {
    const id = this.inputIdValue
    const metaHtml = meta ? `<div class="fu-fsize">${this.escape(meta)}</div>` : ""
    const noteHtml = note
      ? `<div class="fu-current">${this.iconSvg("check", 13)}<span>${this.escape(note)}</span></div>`
      : ""
    const replaceBtn = `<label class="sg-btn sg-btn-outline sg-btn-sm fu-replace" for="${id}">${this.escape(this.replaceLabelValue || "Replace")}</label>`
    const removeBtn = showRemove
      ? `<button class="sg-icon-btn sg-icon-btn-danger fu-remove" type="button" title="${this.escape(this.removeLabelValue || "Remove file")}" aria-label="${this.escape(this.removeLabelValue || "Remove file")}" data-action="file-upload-field#remove">${this.iconSvg("trash-2", 18)}</button>`
      : ""

    this.previewTarget.innerHTML = `
      <span class="fu-thumb">${thumbInner}</span>
      <div class="fu-meta">
        <div class="fu-fname">${this.escape(filename)}</div>
        ${metaHtml}
        ${noteHtml}
      </div>
      <div class="fu-actions">
        ${replaceBtn}
        ${removeBtn}
      </div>`
    this.previewTarget.hidden = false
    this.zoneTarget.hidden = true
  }

  showZone() {
    this.previewTarget.hidden = true
    this.previewTarget.innerHTML = ""
    this.zoneTarget.hidden = false
  }

  // --- helpers -----------------------------------------------------------

  releaseObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  humanSize(bytes) {
    if (!bytes && bytes !== 0) return ""
    const mb = bytes / (1024 * 1024)
    if (mb >= 1) return `${this.round(mb)} MB`
    const kb = bytes / 1024
    if (kb >= 1) return `${Math.round(kb)} KB`
    return `${bytes} B`
  }

  round(n) {
    return Math.round(n * 10) / 10
  }

  // lucide svgs are stroke-based; inline a minimal copy so the controller-built
  // chip matches server-rendered icons without an extra request.
  iconSvg(name, size) {
    const paths = {
      "trash-2":
        '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>',
      "file-text":
        '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>',
      "alert-circle":
        '<circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>',
      check: '<path d="M20 6 9 17l-5-5"/>',
    }
    return `<svg class="lucide-icon lucide-${name}" xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] || ""}</svg>`
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str == null ? "" : String(str)
    return div.innerHTML
  }
}
